import { cache } from '@/container/apollo-client/cache';
import { CURSOR_RECENT_MESSAGE } from '@/graphql/cursor.graphql';
import { AUTH_ME } from '@/graphql/query.graphql';
import { DirectionTypeEnum, MessageConversationSubscription, authMeQuery } from '@/types/generated/graphql';
import { StoreObject, gql } from '@apollo/client';

type RecentMessageFragment = {
  id: number;
  unreadCounter: number | null;
  updatedAt: string;
  markAsClosed: boolean;
  messageConversation: {
    direction: DirectionTypeEnum;
    conversationStatus: 'received';
    messageContent: string;
  };
};

const RecentMessageConversationFragment = gql`
  fragment SubscriptionRecentMessageConversation on RecentMessageConversation {
    markAsClosed
    unreadCounter
    updatedAt
  }
`;

export function recentMessageHandler(data: MessageConversationSubscription, channelId: string, clientId: string) {
  const incomingChannelId = data?.messageConversation?.channelId + '';
  const incomingClientId = data?.messageConversation?.client?.id + '';
  const recentMessageId = `RecentMessageConversation:{"client":{"id":${incomingClientId}},"channel":{"id":${incomingChannelId}}}`;
  const chatIsOpen = channelId === incomingChannelId && clientId === incomingClientId;

  const findRecentMessageConversation = cache.readFragment({
    fragment: RecentMessageConversationFragment,
    id: recentMessageId,
  }) as RecentMessageFragment | null;

  try {
    // create a new data in recent message if the conversation is marked as closed
    // to reopen the conversation
    if (findRecentMessageConversation && !findRecentMessageConversation?.markAsClosed) {
      updateExistingRecentMessage({
        subscriptionData: data,
        findRecentMessageConversation,
        recentMessageId,
        chatIsOpen,
      });
    } else {
      removeRecentMessageFromAllCacheRegistries(incomingChannelId, incomingClientId);
      createNewRecentMessage({ channelId, subscriptionData: data });
    }
  } catch (error) {
    console.error('There was some issue on handleRecentMessage', error);
  }
}

function removeRecentMessageFromAllCacheRegistries(channelId: string, clientId: string) {
  cache.modify({
    id: 'ROOT_QUERY',
    fields: {
      recentMessageConversationsV2(existingRefs = { data: [] }, { readField, storeFieldName }) {
        if (!storeFieldName.includes(`"channelId":${channelId}`) || !storeFieldName.includes('"markAsClosed":true')) {
          return existingRefs;
        }

        const filteredList = existingRefs.data.filter((ref: StoreObject) => {
          const clientRef = readField('client', ref) as StoreObject | undefined;
          const nestedClientId = clientRef ? readField('id', clientRef) : null;
          return String(nestedClientId) !== clientId;
        });

        return {
          ...existingRefs,
          data: filteredList,
        };
      },
    },
  });
}

function updateExistingRecentMessage({
  subscriptionData: data,
  findRecentMessageConversation,
  recentMessageId,
  chatIsOpen,
}: {
  subscriptionData: MessageConversationSubscription;
  findRecentMessageConversation: RecentMessageFragment;
  recentMessageId: string;
  chatIsOpen: boolean;
}) {
  const outgoingMessage = data?.messageConversation?.direction === DirectionTypeEnum.outgoing;
  const profile: authMeQuery | null = cache.readQuery({ query: AUTH_ME });
  const isSelfSender = profile?.authMe?.data?.user?.id === data?.messageConversation?.agent?.user?.id;

  const skipCount = isSelfSender || data?.messageConversation?.conversationStatus === 'initiated' || outgoingMessage;

  try {
    // update recent Message
    cache.writeFragment({
      fragment: RecentMessageConversationFragment,
      id: recentMessageId,
      data: {
        markAsClosed: data?.messageConversation?.markAsClosed,
        updatedAt: data?.messageConversation?.updatedAt,
        unreadCounter:
          chatIsOpen || findRecentMessageConversation?.unreadCounter === null
            ? 0
            : skipCount
              ? findRecentMessageConversation?.unreadCounter
              : findRecentMessageConversation?.unreadCounter + 1,
      },
    });

    // change recent Message's relation with messageConversation
    const recentMessage = cache.identify({
      __typename: 'RecentMessageConversation',
      client: { id: data?.messageConversation?.client?.id },
      channel: { id: data?.messageConversation?.channelId },
    });

    cache.modify({
      id: recentMessage,
      fields: {
        messageConversation() {
          return {
            __ref: 'MessageConversation:' + data?.messageConversation?.id,
          };
        },
      },
    });
  } catch (error) {
    console.error('There was some issue on updateExistingRecentMessage', error);
  }
}

function createNewRecentMessage({
  channelId,
  subscriptionData: data,
}: {
  channelId: string;
  subscriptionData: MessageConversationSubscription;
}) {
  try {
    cache.updateQuery(
      {
        query: CURSOR_RECENT_MESSAGE,
        variables: {
          channelId: +channelId,
          markAsClosed: false,
          query: '',
        },
      },
      (cachedData) => {
        const recentMessageSurrogateId =
          (data?.messageConversation?.client?.id || 1) + (data?.messageConversation?.channelId || 1);
        const newRecentMessage = {
          __typename: 'RecentMessageConversation',
          id: recentMessageSurrogateId,
          lastReadCursor: null,
          draft: {
            mediaCount: 0,
            messageContent: null,
          },
          markAsClosed: false,
          unreadCounter: 1,
          messageConversation: {
            conversationType: 'message',
            conversationStatus: 'received',
            direction: 'incoming',
            messageContent: data?.messageConversation?.messageContent,
            pinned: false,
            voicemailRecordingUrl: data?.messageConversation?.voicemailRecordingUrl,
            numMedia: null,
          },
          pinned: false,
          channel: {
            id: data?.messageConversation?.channelId,
          },
          client: {
            ...data?.messageConversation?.client,
            profilePicture: null,
            country: {
              ...data?.messageConversation?.client?.country,
            },
          },
          updatedAt: data?.messageConversation?.updatedAt,
        };

        return {
          ...cachedData,
          recentMessageConversationsV2: {
            ...cachedData?.recentMessageConversationsV2,
            data: [newRecentMessage, ...(cachedData?.recentMessageConversationsV2?.data ?? [])],
          },
        };
      }
    );
  } catch (error) {
    console.error('There was some issue on createNewRecentMessage', error);
  }
}
